import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WhatsappLogo, CheckCircle, PaperPlaneTilt, Info } from "@phosphor-icons/react";

/** CallMeBot personal config — each user pairs their WhatsApp with the bot
 *  once (send `/start` + code to +34 644 51 95 23), pastes the returned API
 *  key here, and BKflow pushes critical SAV / urgent alerts to their
 *  WhatsApp for free. Client-facing comms stay on Brevo/Twilio.
 */
export default function CallMeBotCard() {
  const { user, refresh } = useAuth();
  const [phone, setPhone] = useState("");
  const [apikey, setApikey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setPhone(user?.callmebot_phone || user?.phone || "");
    setApikey(""); // never prefill — apikey is treated like a secret
    // Auto-expand the card the first time so tech / associé / compta see the
    // pairing steps immediately — critical for SAV/urgent WhatsApp alerts.
    if (user && !user?.callmebot_apikey) setExpanded(true);
  }, [user]);

  const configured = !!user?.callmebot_apikey;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { phone: phone.trim() || null };
      if (apikey.trim()) payload.apikey = apikey.trim();
      await api.patch("/auth/me/callmebot", payload);
      toast.success("CallMeBot enregistré");
      setApikey("");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const testIt = async () => {
    setTesting(true);
    try {
      await api.post("/auth/me/callmebot/test");
      toast.success("WhatsApp de test envoyé — regarde ton téléphone !");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTesting(false); }
  };

  const unlink = async () => {
    if (!window.confirm("Supprimer ta config CallMeBot ? Tu ne recevras plus les alertes internes WhatsApp.")) return;
    try {
      await api.patch("/auth/me/callmebot", { phone: "", apikey: "" });
      await refresh();
      toast.success("CallMeBot délié");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div
      className={`rounded-2xl border p-4 space-y-3 mt-3 ${configured ? "bg-white/[0.03] border-white/10" : "bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.02] border-emerald-500/30"}`}
      data-testid="callmebot-card"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${configured ? "bg-emerald-500" : "bg-emerald-500/20 border border-emerald-500/40"}`}>
          {configured ? <CheckCircle size={20} weight="fill" color="white" /> : <WhatsappLogo size={20} weight="duotone" className="text-emerald-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">
            Alertes WhatsApp perso
            {configured
              ? <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">Actif</span>
              : <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300">À configurer</span>
            }
          </div>
          <div className="text-[10px] text-white/60">Gratuit via CallMeBot · SAV critique + notifs urgentes sur ton WhatsApp</div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[10px] uppercase tracking-widest text-white/60 hover:text-white px-2"
          data-testid="callmebot-toggle"
        >
          {expanded ? "Fermer" : configured ? "Modifier" : "Configurer"}
        </button>
      </div>

      {expanded && (
        <div className="space-y-3 pt-2 border-t border-white/5">
          <div className="rounded-xl bg-[#005CFF]/10 border border-[#005CFF]/25 p-3 text-[11px] text-white/70 space-y-1">
            <div className="flex items-center gap-1.5 text-white font-semibold text-xs">
              <Info size={13} weight="duotone" /> Comment obtenir ta clé (2 min)
            </div>
            <ol className="list-decimal ml-4 space-y-0.5">
              <li>Ajoute <b>+34 644 51 95 23</b> à tes contacts WhatsApp (nom : CallMeBot)</li>
              <li>Envoie-lui <span className="font-mono bg-white/10 px-1 rounded">I allow callmebot to send me messages</span></li>
              <li>Il te répond avec ta clé API perso (ex. <span className="font-mono">1234567</span>)</li>
              <li>Colle-la ci-dessous et enregistre</li>
            </ol>
          </div>

          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Numéro WhatsApp (E.164)</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+33612345678"
              className="bg-white/5 border-white/10 mt-1 font-mono text-sm"
              data-testid="callmebot-phone"
            />
          </div>
          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Clé API CallMeBot</Label>
            <Input
              type="password"
              value={apikey}
              onChange={(e) => setApikey(e.target.value)}
              placeholder={configured ? "Laisser vide pour conserver" : "1234567"}
              className="bg-white/5 border-white/10 mt-1 font-mono text-sm"
              data-testid="callmebot-apikey"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 h-11 rounded-xl" data-testid="callmebot-save">
              {saving ? "…" : "Enregistrer"}
            </Button>
            <Button
              onClick={testIt}
              disabled={testing || !configured}
              variant="ghost"
              className="border border-white/10 hover:bg-white/5 h-11 rounded-xl"
              data-testid="callmebot-test"
            >
              <PaperPlaneTilt size={14} /> {testing ? "…" : "Test"}
            </Button>
          </div>

          {configured && (
            <button
              type="button"
              onClick={unlink}
              className="w-full text-[11px] text-white/40 hover:text-red-300 py-1"
              data-testid="callmebot-unlink"
            >
              Délier CallMeBot
            </button>
          )}
        </div>
      )}
    </div>
  );
}
